import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  Button,
  Spin,
  Form,
  Input,
  Select,
  Space,
  Typography,
  DatePicker,
  TimePicker,
  Popconfirm,
  Row,
  Col,
  message,
  Tabs,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  UserOutlined,
  BankOutlined,
  CheckOutlined,
  CloseOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import { useStore } from '../../stores/root-store-context';
import ERPLayout from '../../components/layout/ERPLayout';
import { CrmEventDto } from '../../api/CRMApiClient';
import CrmAuditInfo from '../../components/crm/CrmAuditInfo';
import CrmHistoryTab from '../../components/crm/CrmHistoryTab';
import CrmAttachmentsTab from '../../components/crm/CrmAttachmentsTab';
import './crm.css';

dayjs.extend(utc);
dayjs.extend(timezone);
const toLocal = (iso: string) => dayjs(iso).local();
const toApi = (d: Dayjs) => d.toISOString();

const { Option } = Select;
const { Title, Text } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

const EventDetail: React.FC = observer(() => {
  const { t } = useTranslation();
  const { eventId } = useParams<{ eventId?: string }>();
  const navigate = useNavigate();
  const { crmStore, applicationStore } = useStore();

  const isNew = !eventId || eventId === 'new';

  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const [editing, setEditing] = useState(isNew || searchParams.get('edit') === '1');
  const [loading, setLoading] = useState(!isNew);
  const [activeTab, setActiveTab] = useState<string>('details');

  const [dateStart, setDateStart] = useState<Dayjs>(dayjs().startOf('hour').add(1, 'hour'));
  const [dateEnd, setDateEnd] = useState<Dayjs>(dayjs().startOf('hour').add(2, 'hour'));

  const [userManuallyEditedName, setUserManuallyEditedName] = useState(!isNew);

  const buildCompositeName = useCallback(
    (companyIds?: number[], contactIds?: number[], typeId?: number, start?: Dayjs) => {
      const parts: string[] = [];
      if (companyIds?.length) {
        const first = crmStore.externalCompanies.find((c) => c.id === companyIds[0]);
        if (first) parts.push(first.name);
        if (companyIds.length > 1) parts.push(`+${companyIds.length - 1}`);
      }
      if (contactIds?.length) {
        const first = crmStore.contacts.find((c) => c.id === contactIds[0]);
        if (first) parts.push(`${first.name} ${first.surname}`.trim());
        if (contactIds.length > 1) parts.push(`+${contactIds.length - 1}`);
      }
      if (typeId) {
        const typeName = crmStore.CrmEventTypes.find((t) => t.id === typeId)?.name;
        if (typeName) parts.push(typeName);
      }
      // ONLY append date if at least one meaningful entity is present
      if (parts.length > 0 && start) {
        parts.push(start.format('DD/MM/YYYY'));
        return parts.join(' — ');
      }
      return '';
    },
    [crmStore.externalCompanies, crmStore.contacts, crmStore.CrmEventTypes]
  );

  useEffect(() => {
    (async () => {
      await Promise.all([
        crmStore.getCrmEventTypes(),
        crmStore.getContactsWithDetails(),
        crmStore.getExternalCompanies(),
      ]);

      if (!isNew && eventId) {
        const id = Number(eventId);
        const evt = await crmStore.getCrmEventById(id);
        if (evt) {
          const s = toLocal(evt.dateStart);
          const e = toLocal(evt.dateEnd);
          setDateStart(s);
          setDateEnd(e);
          form.setFieldsValue({
            ...evt,
            externalCompanyIds: evt.externalCompanies?.map((c) => c.id) ?? [],
            contactIds: evt.contacts?.map((c) => c.id) ?? [],
            name: evt.name,
          });
          setUserManuallyEditedName(true);
        }
      } else {
        const defaultStart = dayjs().startOf('hour').add(1, 'hour');
        const defaultEnd = dayjs().startOf('hour').add(2, 'hour');
        setDateStart(defaultStart);
        setDateEnd(defaultEnd);
      }
      setLoading(false);
    })();
    // eslint-disable-next-line
  }, [eventId]);

  const selectedCompanyIds: number[] = Form.useWatch('externalCompanyIds', form) || [];

  const filteredContacts = useMemo(() => {
    if (!selectedCompanyIds || selectedCompanyIds.length === 0) {
      return crmStore.contacts;
    }
    return crmStore.contacts.filter((c) => {
      const compId = c.externalCompanyId ?? c.externalCompany?.id;
      return compId != null && selectedCompanyIds.includes(compId);
    });
  }, [crmStore.contacts, selectedCompanyIds]);

  const handleValuesChange = (changedValues: any, all: any) => {
    if (!editing) return;
    if (changedValues.name !== undefined) {
      setUserManuallyEditedName(true);
      return;
    }
    if (changedValues.externalCompanyIds) {
      const companyIds: number[] = changedValues.externalCompanyIds || [];
      if (companyIds.length > 0) {
        const currentContactIds: number[] = all.contactIds || [];
        const validContactIds = currentContactIds.filter((id) => {
          const c = crmStore.contacts.find((ct) => ct.id === id);
          const compId = c?.externalCompanyId ?? c?.externalCompany?.id;
          return compId != null && companyIds.includes(compId);
        });
        if (validContactIds.length !== currentContactIds.length) {
          form.setFieldsValue({ contactIds: validContactIds });
        }
      }
    }
    if (!userManuallyEditedName) {
      const composite = buildCompositeName(all.externalCompanyIds, all.contactIds, all.typeId, dateStart);
      if (composite) {
        form.setFieldsValue({ name: composite });
      }
    }
  };

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      const { externalCompanyIds, contactIds, ...rest } = values;

      if (!dateStart || !dateEnd) {
        message.error(t('PleaseSelectDateTime'));
        return;
      }

      const compositeName =
        values.name ||
        buildCompositeName(externalCompanyIds, contactIds, values.typeId, dateStart) ||
        'Event';

      const payload: CrmEventDto = {
        ...rest,
        dateStart: toApi(dateStart),
        dateEnd: toApi(dateEnd),
        externalCompanyIds: externalCompanyIds || [],
        contactIds: contactIds || [],
        name: compositeName,
      };

      const tenant = applicationStore.company?.Tenant || 'notpoint';
      if (isNew) {
        const id = await crmStore.addCrmEvent(payload);
        if (id != null) {
          message.success(t('CreatedSuccessfully'));
          navigate(`/${tenant}/crm/event/${id}`);
        } else {
          message.error(t('SomethingWentWrong'));
        }
      } else if (eventId) {
        const ok = await crmStore.updateCrmEvent(Number(eventId), payload);
        if (ok !== false) {
          message.success(t('SavedSuccessfully'));
          setEditing(false);
          await crmStore.getCrmEventById(Number(eventId));
        } else {
          message.error(t('SomethingWentWrong'));
        }
      }
    } catch (err) {
      console.log('Validation error:', err);
    }
  };

  const onDelete = async () => {
    if (isNew || !eventId) return;
    await crmStore.deleteCrmEvent(Number(eventId));
    navigate(`/${applicationStore.company?.Tenant}/crm/events`);
  };

  const tenantPath = `/${applicationStore.company?.Tenant}`;
  const breadcrumbItems = [
    { label: t('Home'), path: tenantPath },
    { label: 'CRM', path: `${tenantPath}/crm` },
    { label: t('Events'), path: `${tenantPath}/crm/events` },
    {
      label: isNew ? t('AddCrmEvent') : t('EventDetails'),
      path: isNew ? `${tenantPath}/crm/event/new` : `${tenantPath}/crm/event/${eventId}`,
    },
  ];

  /* ───────────────────────────────────── Custom Tag Renders ──────────────────────── */

  const companyTagRender = (props: any) => {
    const { label, closable, onClose } = props;
    return (
      <span className="crm-form-pill-tag">
        <BankOutlined style={{ color: '#2563eb', fontSize: 13, marginRight: 6 }} />
        <span>{label}</span>
        {closable && editing && (
          <CloseOutlined
            onClick={onClose}
            className="crm-form-pill-close"
          />
        )}
      </span>
    );
  };

  const contactTagRender = (props: any) => {
    const { label, closable, onClose } = props;
    return (
      <span className="crm-form-pill-tag">
        <UserOutlined style={{ color: '#2563eb', fontSize: 13, marginRight: 6 }} />
        <span>{label}</span>
        {closable && editing && (
          <CloseOutlined
            onClick={onClose}
            className="crm-form-pill-close"
          />
        )}
      </span>
    );
  };

  return (
    <ERPLayout breadcrumbTitle={t('EventDetails')} breadcrumbItems={breadcrumbItems}>
      <Spin spinning={loading || crmStore.isLoading}>
        <Card
          bordered={false}
          className="crm-event-form-card"
        >
          <div className="crm-detail-top-nav" style={{ marginBottom: 12, display: 'flex', alignItems: 'center' }}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(`/${applicationStore.company?.Tenant}/crm/events`)}
              className="crm-back-button"
            >
              {t('Back')}
            </Button>
          </div>
          {!isNew && <CrmAuditInfo record={crmStore.currentCrmEvent} />}

          <Tabs activeKey={activeTab} onChange={setActiveTab} className="crm-modern-tabs">
            <TabPane tab={t('Details')} key="details">
              <div className="crm-event-editor-container">
                {/* Header title & subtitle */}
                <div className="crm-event-form-header">
                  <div className="crm-event-form-title">
                    {isNew ? t('crm.eventParameters', 'Параметри події') : (crmStore.currentCrmEvent?.name || t('EventDetails'))}
                  </div>
                  <div className="crm-event-form-subtitle">
                    {isNew
                      ? t('crm.eventSubtitle', 'Заповніть дані для планування події CRM')
                      : t('crm.eventEditSubtitle', 'Перегляд та редагування параметрів події CRM')}
                  </div>
                </div>

                <Form
                  form={form}
                  layout="vertical"
                  disabled={!editing}
                  className="crm-event-modern-form"
                  onValuesChange={handleValuesChange}
                >
                  {/* Field 1: Event Name */}
                  <div className="crm-form-group">
                    <div className="crm-form-label-row">
                      <label className="crm-form-label">
                        {t('crm.eventNameLabel', 'НАЗВА ПОДІЇ')} <span className="crm-required">*</span>
                      </label>
                    </div>
                    <Form.Item
                      name="name"
                      rules={[{ required: true, message: t('PleaseEnterEventName', 'Введіть назву події') }]}
                      style={{ marginBottom: 0 }}
                    >
                      <Input
                        placeholder={t('crm.eventNamePlaceholder', 'Тестова компанія — Демонстрація функціоналу')}
                        className="crm-modern-input"
                      />
                    </Form.Item>
                  </div>

                  {/* Field 2: Event Type */}
                  <div className="crm-form-group">
                    <div className="crm-form-label-row">
                      <label className="crm-form-label">
                        {t('crm.eventTypeLabel', 'ТИП ІВЕНТУ')} <span className="crm-required">*</span>
                      </label>
                    </div>
                    <Form.Item
                      name="typeId"
                      rules={[{ required: true, message: t('PleaseSelectEventType', 'Оберіть тип події') }]}
                      style={{ marginBottom: 0 }}
                    >
                      <Select
                        placeholder={t('Select')}
                        allowClear
                        className="crm-modern-select"
                      >
                        {crmStore.CrmEventTypes.map((et) => (
                          <Option key={et.id} value={et.id}>
                            {et.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </div>

                  {/* Field 3: Event Period (Start & End Cards) */}
                  <div className="crm-form-group">
                    <div className="crm-form-label-row">
                      <label className="crm-form-label">
                        {t('crm.periodLabel', 'ПЕРІОД ПРОВЕДЕННЯ (ВІД І ДО)')} <span className="crm-required">*</span>
                      </label>
                    </div>
                    <div className="crm-period-grid">
                      {/* Start Box */}
                      <div className="crm-period-card">
                        <div className="crm-period-card-label">
                          {t('crm.eventStart', 'Початок події')}
                        </div>
                        <div className="crm-period-card-inputs">
                          <DatePicker
                            value={dateStart}
                            onChange={(d) => {
                              if (!d) return;
                              const updated = (dateStart || dayjs())
                                .year(d.year())
                                .month(d.month())
                                .date(d.date());
                              setDateStart(updated);
                              if (dateEnd && dateEnd.isBefore(updated)) {
                                setDateEnd(updated.add(1, 'hour'));
                              }
                            }}
                            format="DD/MM/YYYY"
                            allowClear={false}
                            disabled={!editing}
                            className="crm-period-input-date"
                            suffixIcon={<CalendarOutlined style={{ color: '#64748b' }} />}
                          />
                          <TimePicker
                            value={dateStart}
                            onChange={(tVal) => {
                              if (!tVal) return;
                              const updated = (dateStart || dayjs())
                                .hour(tVal.hour())
                                .minute(tVal.minute())
                                .second(0);
                              setDateStart(updated);
                              if (dateEnd && dateEnd.isBefore(updated)) {
                                setDateEnd(updated.add(1, 'hour'));
                              }
                            }}
                            format="HH:mm"
                            allowClear={false}
                            disabled={!editing}
                            className="crm-period-input-time"
                            suffixIcon={<ClockCircleOutlined style={{ color: '#64748b' }} />}
                          />
                        </div>
                      </div>

                      {/* End Box */}
                      <div className="crm-period-card">
                        <div className="crm-period-card-label">
                          {t('crm.eventEnd', 'Закінчення події')}
                        </div>
                        <div className="crm-period-card-inputs">
                          <DatePicker
                            value={dateEnd}
                            onChange={(d) => {
                              if (!d) return;
                              const updated = (dateEnd || dayjs())
                                .year(d.year())
                                .month(d.month())
                                .date(d.date());
                              setDateEnd(updated);
                            }}
                            format="DD/MM/YYYY"
                            allowClear={false}
                            disabled={!editing}
                            className="crm-period-input-date"
                            suffixIcon={<CalendarOutlined style={{ color: '#64748b' }} />}
                          />
                          <TimePicker
                            value={dateEnd}
                            onChange={(tVal) => {
                              if (!tVal) return;
                              const updated = (dateEnd || dayjs())
                                .hour(tVal.hour())
                                .minute(tVal.minute())
                                .second(0);
                              setDateEnd(updated);
                            }}
                            format="HH:mm"
                            allowClear={false}
                            disabled={!editing}
                            className="crm-period-input-time"
                            suffixIcon={<ClockCircleOutlined style={{ color: '#64748b' }} />}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Field 4: Companies Multi-select */}
                  <div className="crm-form-group">
                    <div className="crm-form-label-row">
                      <label className="crm-form-label">
                        {t('crm.chooseCompaniesLabel', 'ВИБІР КОМПАНІЙ (ОДНА АБО ДЕКІЛЬКА)')} <span className="crm-required">*</span>
                      </label>
                      <span className="crm-form-label-hint">
                        {t('crm.multiSelect', 'Мультивибір')}
                      </span>
                    </div>
                    <Form.Item
                      name="externalCompanyIds"
                      rules={[{ required: true, message: t('PleaseSelectCompany', 'Оберіть хоча б одну компанію') }]}
                      style={{ marginBottom: 0 }}
                    >
                      <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        tagRender={companyTagRender}
                        placeholder={t('crm.chooseCompanyPlaceholder', '+ Оберіть або введіть компанію...')}
                        className="crm-modern-multi-select"
                      >
                        {crmStore.externalCompanies.map((c) => (
                          <Option key={c.id} value={c.id}>
                            {c.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </div>

                  {/* Field 5: Contacts Multi-select */}
                  <div className="crm-form-group">
                    <div className="crm-form-label-row">
                      <label className="crm-form-label">
                        {t('crm.chooseContactsLabel', 'ПРИВ\'ЯЗКА КОНТАКТІВ ДО ПОДІЇ (ОДИН АБО ДЕКІЛЬКА)')}
                      </label>
                      <span className="crm-form-label-hint">
                        {t('crm.multiSelect', 'Мультивибір')}
                      </span>
                    </div>
                    <Form.Item name="contactIds" style={{ marginBottom: 0 }}>
                      <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        tagRender={contactTagRender}
                        placeholder={t('crm.chooseContactPlaceholder', '+ Додати контакт...')}
                        className="crm-modern-multi-select"
                      >
                        {filteredContacts.map((c) => (
                          <Option key={c.id} value={c.id}>
                            {c.name} {c.surname}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </div>

                  {/* Field 6: Description */}
                  <div className="crm-form-group">
                    <div className="crm-form-label-row">
                      <label className="crm-form-label">
                        {t('crm.eventDescLabel', 'ОПИС ПОДІЇ')}
                      </label>
                    </div>
                    <Form.Item name="description" style={{ marginBottom: 0 }}>
                      <TextArea
                        rows={5}
                        placeholder={t('crm.eventDescPlaceholder', 'Введіть детальний опис або план обговорення...')}
                        className="crm-modern-textarea"
                      />
                    </Form.Item>
                  </div>

                  {/* Action Buttons in footer */}
                  <div className="crm-event-form-actions">
                    <div className="crm-event-actions-left">
                      {!isNew && (
                        <Popconfirm title={t('DeleteEventConfirm')} onConfirm={onDelete}>
                          <Button danger icon={<DeleteOutlined />} className="crm-form-delete-btn">
                            {t('Delete')}
                          </Button>
                        </Popconfirm>
                      )}
                    </div>

                    <div className="crm-event-actions-right">
                      <Button
                        className="crm-form-cancel-btn"
                        onClick={() => navigate(`${tenantPath}/crm/events`)}
                      >
                        {t('Cancel')}
                      </Button>

                      {editing ? (
                        <Button
                          type="primary"
                          className="crm-form-submit-btn"
                          icon={<CheckOutlined />}
                          onClick={onSave}
                        >
                          {isNew ? t('crm.createEventBtn', 'Створити подію') : t('crm.saveChangesBtn', 'Зберегти зміни')}
                        </Button>
                      ) : (
                        <Button
                          type="primary"
                          className="crm-form-submit-btn"
                          icon={<EditOutlined />}
                          onClick={() => setEditing(true)}
                        >
                          {t('Edit')}
                        </Button>
                      )}
                    </div>
                  </div>
                </Form>

                {/* Related Entities info when in view mode */}
                {!editing && !isNew && (
                  <div className="crm-event-related-section">
                    <Title level={5}>{t('RelatedEntities')}</Title>

                    {crmStore.currentCrmEvent?.contacts && crmStore.currentCrmEvent.contacts.length > 0 && (
                      <Card
                        size="small"
                        style={{ marginBottom: 16 }}
                        title={
                          <Space>
                            <UserOutlined />
                            {t('Contacts')} ({crmStore.currentCrmEvent.contacts.length})
                          </Space>
                        }
                      >
                        {crmStore.currentCrmEvent.contacts.map((c, idx) => (
                          <div
                            key={c.id}
                            style={{
                              marginBottom: idx < crmStore.currentCrmEvent!.contacts!.length - 1 ? 12 : 0,
                              borderBottom:
                                idx < crmStore.currentCrmEvent!.contacts!.length - 1
                                  ? '1px solid #f0f0f0'
                                  : 'none',
                              paddingBottom: idx < crmStore.currentCrmEvent!.contacts!.length - 1 ? 8 : 0,
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <strong>
                                {c.name} {c.surname}
                              </strong>
                              <Button
                                type="link"
                                size="small"
                                onClick={() => navigate(`${tenantPath}/crm/contact/${c.id}`)}
                              >
                                {t('View')}
                              </Button>
                            </div>
                            {c.email && (
                              <p style={{ margin: 0 }}>
                                <strong>{t('Email')}:</strong> {c.email}
                              </p>
                            )}
                            {c.phoneNumber && (
                              <p style={{ margin: 0 }}>
                                <strong>{t('Phone')}:</strong> {c.phoneNumber}
                              </p>
                            )}
                          </div>
                        ))}
                      </Card>
                    )}

                    {crmStore.currentCrmEvent?.externalCompanies &&
                      crmStore.currentCrmEvent.externalCompanies.length > 0 && (
                        <Card
                          size="small"
                          style={{ marginBottom: 16 }}
                          title={
                            <Space>
                              <BankOutlined />
                              {t('Companies')} ({crmStore.currentCrmEvent.externalCompanies.length})
                            </Space>
                          }
                        >
                          {crmStore.currentCrmEvent.externalCompanies.map((comp, idx) => (
                            <div
                              key={comp.id}
                              style={{
                                marginBottom:
                                  idx < crmStore.currentCrmEvent!.externalCompanies!.length - 1 ? 12 : 0,
                                borderBottom:
                                  idx < crmStore.currentCrmEvent!.externalCompanies!.length - 1
                                    ? '1px solid #f0f0f0'
                                    : 'none',
                                paddingBottom:
                                  idx < crmStore.currentCrmEvent!.externalCompanies!.length - 1 ? 8 : 0,
                              }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <strong>{comp.name}</strong>
                                <Button
                                  type="link"
                                  size="small"
                                  onClick={() => navigate(`${tenantPath}/crm/company/${comp.id}`)}
                                >
                                  {t('View')}
                                </Button>
                              </div>
                              {comp.edrpou && (
                                <p style={{ margin: 0 }}>
                                  <strong>{t('EDRPOU')}:</strong> {comp.edrpou}
                                </p>
                              )}
                              {comp.addresses?.[0] && (
                                <p style={{ margin: 0 }}>
                                  <strong>{t('Address')}:</strong>{' '}
                                  {[
                                    comp.addresses[0].locationName,
                                    comp.addresses[0].city,
                                    comp.addresses[0].country,
                                  ]
                                    .filter(Boolean)
                                    .join(', ')}
                                </p>
                              )}
                            </div>
                          ))}
                        </Card>
                      )}
                  </div>
                )}
              </div>
            </TabPane>

            {!isNew && eventId && (
              <TabPane tab={t('Files')} key="files">
                <CrmAttachmentsTab
                  entityType="Event"
                  entityId={Number(eventId)}
                  active={activeTab === 'files'}
                />
              </TabPane>
            )}

            {!isNew && eventId && (
              <TabPane tab={t('History')} key="history">
                <CrmHistoryTab
                  entityType="Event"
                  entityId={Number(eventId)}
                  active={activeTab === 'history'}
                />
              </TabPane>
            )}
          </Tabs>
        </Card>
      </Spin>
    </ERPLayout>
  );
});

export default EventDetail;
